import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { LoaderService } from 'src/app/core/header-menu/loader/loader.service';
import { Product } from 'src/app/models/interface/product/product';
import { FtthResourceService } from 'src/app/services/api/ftth-resource/ftth-resource.service';
import { FtthService } from 'src/app/services/api/ftth/ftth.service';
import { NetworkService } from 'src/app/services/api/network/network.service';
import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { ProductDepthLevelCfsActionsComponent } from '../product-depth-level-cfs-actions.component';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';

@Component({
    selector: 'app-move-cto',
    templateUrl: './move-cto.component.html',
    styleUrls: ['./move-cto.component.scss']
})
export class MoveCtoComponent implements OnInit, OnDestroy {

    oltsSubject: Subject<String[]> = new Subject();
    olts = this.oltsSubject.asObservable();

    currentOlt: string;
    currentOltSlot: string;
    currentOltPort: string;
    affectedCustomers: string[];

    loading: boolean;

    searchGroup: FormGroup;

    private subscriptions = new Subscription();

    constructor(
        private ftthService: FtthService,
        private ftthResourceService: FtthResourceService,
        private networkService: NetworkService,
        private loaderService: LoaderService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private formBuilder: FormBuilder,
        private dialogRef: MatDialogRef<ProductDepthLevelCfsActionsComponent>,
        @Inject(MAT_DIALOG_DATA) private data: {
            item: Product;
            type: string;
        },
    ) {
    }

    ngOnInit() {
        if (this.data.type !== 'cfs') {
            throw new Error('It must be a CFS');
        }

        this.currentOlt = this.data.item.cfses[0].cfsCharValues.find(charValue => charValue.specId === 'sc_network_olt_code').value;
        this.currentOltSlot = this.data.item.cfses[0].cfsCharValues.find(charValue => charValue.specId === 'sc_network_olt_card').value
        this.currentOltPort = this.data.item.cfses[0].cfsCharValues.find(charValue => charValue.specId === 'sc_network_olt_port').value

        this.subscriptions.add(this.loaderService.loading$.subscribe(loading => this.loading = loading));

        this.searchGroup = this.formBuilder.group({
            olt: this.formBuilder.control('', [Validators.required]),
            oltSlot: this.formBuilder.control('', [Validators.required, Validators.min(1), Validators.max(99)]),
            oltPort: this.formBuilder.control('', [Validators.required, Validators.min(1), Validators.max(99)]),
        });

        this.affectedCustomers = [];

        this.getAffectedCustomers()
        this.getOLTCombos();

    }

    getAffectedCustomers() {
        this.ftthResourceService.getAffectedCustomers(this.currentOlt, this.currentOltSlot, this.currentOltPort)
            .subscribe(
                (data: any) => {
                    for (const customer of data) {
                        this.affectedCustomers.push(customer.iua)
                    }
                },
            );
    }

    getOLTCombos() {
        if(this.currentOlt){
            this.oltsSubject.next([this.currentOlt])
            this.oltControl.setValue(this.currentOlt);
            this.oltSlotControl.setValue(this.currentOltSlot);
            this.oltPortControl.setValue(this.currentOltPort);
        }

        this.oltControl!.valueChanges.pipe(
            distinctUntilChanged(),
            debounceTime(500),
            filter((name) => !!name && name.length >= 3),
            switchMap(name => this.ftthResourceService.getResourceBySpecNameLifecycleOrdered(name,'OLTResSpec',0,50,null,null))
          ).subscribe(data => {
            this.oltsSubject.next(data.resources.map(r => r.name.replace(/OLT-/g, '')));
          });
    }

    get oltControl(): FormControl {
        return this.searchGroup.get('olt') as FormControl;
    }

    get oltSlotControl(): FormControl {
        return this.searchGroup.get('oltSlot') as FormControl;
    }

    get oltPortControl(): FormControl {
        return this.searchGroup.get('oltPort') as FormControl;
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    moveCTOAndReprovision() {
        if (this.oltSlotControl.value < 1 || this.oltSlotControl.value > 99 || this.oltPortControl.value < 1 || this.oltSlotControl.value > 99) {
            this.notificationsService.showNotification(this.translateService.instant('MOVE_CTO.oltSlotAndCardRange'), NotificationLevel.ERROR)
            return;
        }
        const idAdministrative = this.data.item.cfses[0].cfsCharValues.find(charValue => charValue.specId === 'sc_network_service_id').value.replace('A', '');
        const ctoCode = this.data.item.cfses[0].cfsCharValues.find(charValue => charValue.specId === 'sc_network_cto_code').value;
        this.subscriptions.add(this.networkService.moveCto(idAdministrative, this.oltControl.value, this.oltSlotControl.value, this.oltPortControl.value)
            .subscribe(
                (data) => {
                    this.dialogRef.close(true);
                    this.notificationsService.showNotification(
                        this.translateService.instant('MOVE_CTO.success'),
                        NotificationLevel.SUCCESS);
                },
                (error) => {
                    this.notificationsService.showNotification("ERROR: " + error.error.errorDesc.description ? error.error.errorDesc.description : error.error.errorDesc, NotificationLevel.ERROR);
                }
            ));
    }

}
